import { useAccessContext } from '../context/AccessContext'
import type { AccessContext, MarketDemandCapabilities, MarketDemandFeatureState } from '../types/access'

const failClosedCapabilities: MarketDemandCapabilities = Object.freeze({
  featureStates: {},
  canUseExternalJobBank: false,
  canUseExternalJobMatching: false,
  canClaimExternalJobs: false,
  companyClaimOrganizationIds: [],
  canUseAgencyMarketOpportunities: false,
  agencyOrganizationIds: [],
  canConvertExternalJobs: false,
  canManageCommunitySignals: false,
  canViewMarketIntelligence: false,
  canViewWorkforceIntelligence: false,
  canUseAdvancedReporting: false,
  canUseAdvancedMatching: false,
  canAdministerExternalJobFeeds: false,
})

/** Uses only backend-authored access context; React never derives plan rules. */
export const resolveMarketDemandCapabilities = (accessContext?: AccessContext | null): MarketDemandCapabilities => (
  accessContext?.uiCapabilities?.marketDemand
  || accessContext?.featureFlagsOrUiCapabilities?.marketDemand
  || failClosedCapabilities
)

export const useMarketDemandCapabilities = (): MarketDemandCapabilities => {
  const { accessContext } = useAccessContext()
  return resolveMarketDemandCapabilities(accessContext)
}

/**
 * Shared disabled-state semantics for future Market Demand surfaces. Copy lives
 * here once so screens never restate commercial rules or invent new wording.
 * `visible: false` means an ordinary tenant must not see the surface at all.
 */
export interface MarketDemandCapabilityPresentation {
  enabled: boolean
  visible: boolean
  label: string | null
  action: 'none' | 'contact_sales' | 'select_plan' | 'checkout'
  contactSalesUrl: string | null
}

export const describeMarketDemandCapability = (
  capabilities: MarketDemandCapabilities,
  featureKey: string
): MarketDemandCapabilityPresentation => {
  const state: MarketDemandFeatureState | undefined = capabilities.featureStates?.[featureKey]
  if (state?.enabled) {
    return { enabled: true, visible: true, label: null, action: 'none', contactSalesUrl: null }
  }
  const contactSalesUrl = state?.contactSalesUrl ?? null
  const upgradeAction = state?.upgradeAction ?? 'contact_sales'
  if (!state || state.reason === 'platform_only') {
    return { enabled: false, visible: false, label: null, action: 'none', contactSalesUrl: null }
  }
  if (state.reason === 'disabled_by_platform') {
    return { enabled: false, visible: true, label: 'Disabled by Platform Admin', action: 'none', contactSalesUrl: null }
  }
  if (state.reason === 'upgrade_required') {
    return {
      enabled: false,
      visible: true,
      label: upgradeAction === 'contact_sales' ? 'Contact Sales' : 'Upgrade Required',
      action: upgradeAction === 'none' ? 'contact_sales' : upgradeAction,
      contactSalesUrl,
    }
  }
  return { enabled: false, visible: true, label: 'Contact Sales', action: 'contact_sales', contactSalesUrl }
}
